// src/components/Screens/CreateWorkshopPanel.jsx
import React, { useState, useId, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useWorkshopCreation } from "../../hooks/useWorkshopCreation";
import { useTheme } from "../../core/theme/ThemeProvider";
import { useMaterials } from "../../contexts/MaterialContext";

const newRowId = () =>
  globalThis.crypto?.randomUUID?.() ||
  `row_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const emptyMaterial = { id: newRowId(), name: "", qty: "", unit: "g" };

// Styling classes
const glassStyle = (isDark) => ({
  background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.60)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: isDark ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(0,0,0,0.08)",
  borderRadius: "24px",
  boxShadow:
    "0 18px 45px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.18)",
  transition: "background-color 200ms, border-color 200ms, box-shadow 200ms",
});

export default function CreateWorkshopPanel({ 
  isDark = false, 
  onWorkshopCreated,
  initialData = null  // For duplicating existing workshops
}) {
  const { handleCreateWorkshop } = useWorkshopCreation();
  const { mode } = useTheme();
  const { materials = [], scrapedMaterials = [] } = useMaterials();
  const inv = (scrapedMaterials?.length ? scrapedMaterials : materials) || [];
  const datalistId = useId();

  // Form state - initialize with provided data or defaults
  const [form, setForm] = useState(() => {
    if (initialData) {
      return {
        title: initialData.title || "",
        description: initialData.description || "",
        date: initialData.date || "",
        time: initialData.time || "",
        duration_minutes: Number(initialData.duration_minutes) || 120,
        capacity: Number(initialData.capacity) || 10,
        price: Number(initialData.price) || 80,
        discountPercent: Number(initialData.discountPercent) || 0,
        type: initialData.type || "Hand-building",
        level: initialData.level || "All Levels",
        materialsPerPerson: Array.isArray(initialData.materialsPerPerson)
          ? initialData.materialsPerPerson.map(m => ({ ...m, id: newRowId() }))
          : [{ ...emptyMaterial }],
        teaserDataUrl: initialData.teaserDataUrl || null,
        isPublic: initialData.isPublic !== false,
        isRecurring: initialData.isRecurring || false,
        recurringType: initialData.recurringType || "weekly",
        recurringCount: Number(initialData.recurringCount) || 4,
      };
    }
    return {
      title: "",
      description: "",
      date: "",
      time: "",
      duration_minutes: 120,
      capacity: 10,
      price: 80,
      discountPercent: 0,
      type: "Hand-building",
      level: "All Levels",
      materialsPerPerson: [{ ...emptyMaterial }],
      teaserDataUrl: null,
      isPublic: true,
      isRecurring: false,
      recurringType: "weekly",
      recurringCount: 4,
      recurringDays: [],
    };
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Styling
  const isDarkMode = mode === "dark" || isDark;
  const inputClass = `w-full border rounded px-3 py-2 bg-slate-900/40 border-white/10 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[var(--mudo-primary)] focus:ring-1 focus:ring-[var(--mudo-primary)]`;
  const selectClass = inputClass;
  const smallLabel = "text-[11px] text-slate-300 font-semibold uppercase tracking-wide";
  const fieldLabel = "text-sm text-slate-100 font-medium mb-1";
  const cardClass = "rounded-lg border bg-slate-800/60 border-white/10 p-4 space-y-4";

  // Materials total calculation
  const materialsTotal = useMemo(() => {
    return (form.materialsPerPerson || []).map((m) => ({
      ...m,
      total: Math.max(0, (Number(m.qty) || 0) * Math.max(1, Number(form.capacity) || 0)),
    }));
  }, [form.materialsPerPerson, form.capacity]);

  // Handlers
  const updateForm = (field, value) => setForm((s) => ({ ...s, [field]: value }));

  const updateMaterial = (idx, field, value) => {
    setForm((s) => ({
      ...s,
      materialsPerPerson: s.materialsPerPerson.map((m, i) =>
        i === idx ? { ...m, [field]: value } : m
      ),
    }));
  };

  const addMaterial = () => {
    setForm((s) => ({
      ...s,
      materialsPerPerson: [...s.materialsPerPerson, { ...emptyMaterial, id: newRowId() }],
    }));
  };

  const removeMaterial = (idx) => {
    setForm((s) => ({
      ...s,
      materialsPerPerson: s.materialsPerPerson.filter((_, i) => i !== idx),
    }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be smaller than 5MB");
      setTimeout(() => setError(null), 4000);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setForm((s) => ({ ...s, teaserDataUrl: reader.result }));
      setError(null);
    };
    reader.onerror = () => {
      setError("Failed to read image file");
      setTimeout(() => setError(null), 4000);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setForm((s) => ({ ...s, teaserDataUrl: null }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file?.type.startsWith("image/")) {
      handleImageSelect({ target: { files: [file] } });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.title.trim() || !form.date || !form.time) {
      setError("Title, date, and time are required");
      return;
    }

    try {
      setLoading(true);

      if (form.isRecurring) {
        // Create multiple workshops based on recurring settings
        const baseDate = new Date(form.date + 'T' + form.time);
        const workshopsToCreate = [];
        const selectedDays = form.recurringDays || [baseDate.getDay()];
        let createdCount = 0;
        let currentDate = new Date(baseDate);

        while (createdCount < form.recurringCount) {
          const currentDay = currentDate.getDay();

          // For weekly recurring, only create if current day matches selected days
          const shouldCreate = form.recurringType === "weekly" 
            ? selectedDays.includes(currentDay)
            : true;

          if (shouldCreate) {
            const formattedDate = currentDate.toISOString().split('T')[0];
            
            workshopsToCreate.push({
              ...form,
              date: formattedDate,
              title: form.recurringCount > 1 ? `${form.title} (${createdCount + 1}/${form.recurringCount})` : form.title,
              isRecurring: false,
              recurringDays: undefined,
            });
            createdCount++;
          }

          // Advance date based on frequency
          if (form.recurringType === 'daily') {
            currentDate.setDate(currentDate.getDate() + 1);
          } else if (form.recurringType === 'weekly') {
            currentDate.setDate(currentDate.getDate() + 1);
          } else if (form.recurringType === 'biweekly') {
            currentDate.setDate(currentDate.getDate() + 1);
          } else if (form.recurringType === 'monthly') {
            currentDate.setMonth(currentDate.getMonth() + 1);
          }
        }

        // Create all workshops
        let successCount = 0;
        for (const workshopData of workshopsToCreate) {
          await handleCreateWorkshop(workshopData, { silent: true });
          successCount++;
        }

        // Success notification
        setError(null);
        if (typeof onWorkshopCreated === "function") {
          onWorkshopCreated({ message: `Created ${successCount} recurring workshops` });
        }
      } else {
        // Single workshop creation
        await handleCreateWorkshop(form, {
          onSuccess: (workshop) => {
            if (typeof onWorkshopCreated === "function") {
              onWorkshopCreated(workshop);
            }
          },
        });
      }

      // Reset form
      setForm({
        title: "",
        description: "",
        date: "",
        time: "",
        duration_minutes: 120,
        capacity: 10,
        price: 80,
        discountPercent: 0,
        type: "Hand-building",
        level: "All Levels",
        materialsPerPerson: [{ ...emptyMaterial }],
        teaserDataUrl: null,
        isRecurring: false,
        recurringType: "weekly",
        recurringCount: 4,
        recurringDays: [],
      });
    } catch (err) {
      // ⚠️ Show prominent error if Supabase save failed
      const errorMsg = err?.message || "Failed to create workshop";
      console.error('[CreateWorkshopPanel] Submit failed:', { error: err, msg: errorMsg });
      setError(errorMsg);
      // Keep error visible until user dismisses or fixes it
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      className={`rounded-xl p-4 space-y-4 ${isDarkMode ? "bg-slate-900/40" : "bg-white/20"}`}
      style={glassStyle(isDarkMode)}
    >
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-white">Create Workshop</h2>
        <p className="text-xs text-slate-400 mt-1">
          Set up a new workshop with materials and pricing
        </p>
      </div>

      {/* Error alert - prominent for Supabase failures */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="bg-red-900/60 border-2 border-red-500 rounded-lg p-4 space-y-2"
          >
            <div className="flex items-start gap-2">
              <span className="text-red-300 font-bold text-lg">⚠️</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-100">Workshop Creation Failed</p>
                <p className="text-sm text-red-200 mt-1">{error}</p>
              </div>
            </div>
            <p className="text-xs text-red-300 ml-7">Check your internet connection and try again.</p>
          </motion.div>
        )}
      </AnimatePresence>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* ===== Basic Info Section ===== */}
        <div className={cardClass}>
          <div className={smallLabel}>Basic Information</div>

          <label className="block">
            <div className={fieldLabel}>Title *</div>
            <input
              type="text"
              value={form.title}
              onChange={(e) => updateForm("title", e.target.value)}
              placeholder="e.g., Hand-Building Basics"
              className={inputClass}
            />
          </label>

          <label className="block">
            <div className={fieldLabel}>Description</div>
            <textarea
              value={form.description}
              onChange={(e) => updateForm("description", e.target.value)}
              placeholder="Describe what participants will learn..."
              rows={3}
              className={`${inputClass} resize-none`}
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className={fieldLabel}>Date *</div>
              <input
                type="date"
                value={form.date}
                onChange={(e) => updateForm("date", e.target.value)}
                className={inputClass}
              />
            </label>
            <label className="block">
              <div className={fieldLabel}>Time *</div>
              <input
                type="time"
                value={form.time}
                onChange={(e) => updateForm("time", e.target.value)}
                className={inputClass}
              />
            </label>

            {/* Duration selector with end time preview */}
            <div className="space-y-3">
              <label className="block">
                <div className={fieldLabel}>Duration (30-min increments) *</div>
                <div className="flex flex-wrap gap-2">
                  {[30, 60, 90, 120, 150, 180, 210, 240].map((mins) => (
                    <button
                      key={mins}
                      type="button"
                      onClick={() => updateForm("duration_minutes", mins)}
                      className={`px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                        form.duration_minutes === mins
                          ? "bg-[var(--mudo-primary)] text-slate-900 shadow-lg shadow-amber-400/20"
                          : "bg-slate-700/50 border border-white/20 text-slate-100 hover:bg-slate-600/50"
                      }`}
                    >
                      {mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ""}`}
                    </button>
                  ))}
                </div>
              </label>

              {/* End time preview */}
              {form.date && form.time && (
                <div className="text-xs bg-slate-800/40 border border-white/10 rounded-lg p-3 space-y-1">
                  {(() => {
                    const startDt = new Date(form.date + "T" + form.time);
                    const endDt = new Date(startDt.getTime() + form.duration_minutes * 60000);
                    const formatter = new Intl.DateTimeFormat("en-AU", {
                      timeZone: "Australia/Sydney",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    });
                    return (
                      <>
                        <p className="text-slate-300">
                          <strong>Starts:</strong> {formatter.format(startDt)}
                        </p>
                        <p className="text-slate-300">
                          <strong>Ends:</strong> {formatter.format(endDt)}
                        </p>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Recurring option */}
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isRecurring}
                onChange={(e) => updateForm("isRecurring", e.target.checked)}
                className="w-4 h-4 rounded border-white/20 bg-slate-800 text-[var(--mudo-primary)] focus:ring-[var(--mudo-primary)] focus:ring-offset-0"
              />
              <span className={fieldLabel}>Recurring Workshop</span>
            </label>

            <AnimatePresence>
              {form.isRecurring && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3"
                >
                  <label className="block">
                    <div className="text-xs text-slate-300 mb-1">Frequency</div>
                    <select
                      value={form.recurringType}
                      onChange={(e) => updateForm("recurringType", e.target.value)}
                      className={`${selectClass} text-slate-900`}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </label>

                  {form.recurringType === "weekly" && (
                    <div>
                      <div className="text-xs text-slate-300 mb-2">Days of Week</div>
                      <div className="grid grid-cols-4 gap-2">
                        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, idx) => (
                          <label key={day} className="flex items-center gap-1 cursor-pointer p-2 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 transition-colors">
                            <input
                              type="checkbox"
                              checked={(form.recurringDays || []).includes(idx)}
                              onChange={(e) => {
                                const days = form.recurringDays || [];
                                if (e.target.checked) {
                                  updateForm("recurringDays", [...new Set([...days, idx])].sort());
                                } else {
                                  updateForm("recurringDays", days.filter((d) => d !== idx));
                                }
                              }}
                              className="w-3 h-3 accent-amber-400"
                            />
                            <span className="text-xs text-white/90 font-medium">{day}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <label className="block">
                    <div className="text-xs text-slate-300 mb-1">Number of sessions</div>
                    <input
                      type="number"
                      min="1"
                      max="52"
                      value={form.recurringCount}
                      onChange={(e) => updateForm("recurringCount", Math.max(1, Math.min(52, Number(e.target.value))))}
                      className={inputClass}
                    />
                  </label>
                </motion.div>
              )}
            </AnimatePresence>

            {form.isRecurring && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-xs text-slate-400 bg-slate-800/40 border border-white/5 rounded-lg p-2"
              >
                💡 This will create {form.recurringCount} workshop{form.recurringCount > 1 ? 's' : ''} {form.recurringType === 'daily' ? 'every day' : form.recurringType === 'weekly' ? `every week on ${(form.recurringDays || []).map(d => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d]).join(", ")}` : form.recurringType === 'biweekly' ? 'every 2 weeks' : 'every month'} starting from {form.date || 'the selected date'}.
              </motion.div>
            )}
          </div>

          {/* Image uploader */}
          <div className="space-y-2">
            <div className={fieldLabel}>Teaser Image</div>
            {form.teaserDataUrl ? (
              <div className="space-y-2">
                <div className="relative rounded-lg overflow-hidden bg-slate-800/50 border border-white/10">
                  <img
                    src={form.teaserDataUrl}
                    alt="Workshop teaser"
                    className="w-full h-40 object-cover"
                  />
                </div>
                <button
                  type="button"
                  onClick={removeImage}
                  className="w-full px-3 py-2 text-sm rounded border border-red-500/50 text-red-300 hover:bg-red-900/20 transition"
                >
                  Remove Image
                </button>
              </div>
            ) : (
              <label
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-white/20 p-6 cursor-pointer hover:border-white/40 transition"
              >
                <div className="text-3xl">📷</div>
                <div>
                  <p className="text-sm text-white font-medium">Upload image</p>
                  <p className="text-xs text-slate-400">or drag and drop</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </label>
            )}
            
            {/* Canva Helper Tip */}
            <div className="mt-3 p-3 rounded-lg bg-blue-900/20 border border-blue-500/30">
              <div className="flex items-start gap-2">
                <span className="text-lg">💡</span>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-blue-200">Tip: Use Canva to create</p>
                  <p className="text-xs text-blue-300">
                    Create professional workshop images with{" "}
                    <a
                      href="https://www.canva.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-blue-100 transition"
                    >
                      Canva.com
                    </a>
                    {" "}using pre-made templates. Export as PNG/JPG, then upload here.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Pricing & Capacity Section ===== */}
        <div className={cardClass}>
          <div className={smallLabel}>Pricing & Capacity</div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className={fieldLabel}>Capacity</div>
              <input
                type="number"
                min="1"
                value={form.capacity}
                onChange={(e) => updateForm("capacity", e.target.value)}
                className={inputClass}
              />
            </label>
            <label className="block">
              <div className={fieldLabel}>Price (AUD)</div>
              <input
                type="number"
                min="0"
                step="5"
                value={form.price}
                onChange={(e) => updateForm("price", e.target.value)}
                className={inputClass}
              />
            </label>
          </div>

          <label className="block">
            <div className={fieldLabel}>App Discount %</div>
            <input
              type="number"
              min="0"
              max="100"
              step="5"
              value={form.discountPercent}
              onChange={(e) => updateForm("discountPercent", e.target.value)}
              className={inputClass}
            />
            <p className="text-xs text-slate-400 mt-1">
              Final price: ${(
                form.price * (1 - form.discountPercent / 100)
              ).toFixed(2)}
            </p>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className={fieldLabel}>Type</div>
              <select
                value={form.type}
                onChange={(e) => updateForm("type", e.target.value)}
                className={selectClass}
              >
                <option>Hand-building</option>
                <option>Wheel Throwing</option>
                <option>Glazing</option>
                <option>Sculpture</option>
                <option>General</option>
              </select>
            </label>
            <label className="block">
              <div className={fieldLabel}>Level</div>
              <select
                value={form.level}
                onChange={(e) => updateForm("level", e.target.value)}
                className={selectClass}
              >
                <option>All Levels</option>
                <option>Beginner</option>
                <option>Intermediate</option>
                <option>Advanced</option>
              </select>
            </label>
          </div>


        </div>

        {/* ===== Materials Section ===== */}
        <div className={cardClass}>
          <div className="flex items-center justify-between">
            <div className={smallLabel}>Materials Per Participant</div>
            <button
              type="button"
              onClick={addMaterial}
              className="text-xs px-2 py-1 rounded bg-blue-600/70 text-white hover:bg-blue-600 transition"
            >
              + Add Material
            </button>
          </div>

          {materialsTotal.length > 0 ? (
            <div className="space-y-2">
              {materialsTotal.map((mat, idx) => (
                <motion.div
                  key={mat.id}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="rounded border border-white/10 bg-slate-900/30 p-3 space-y-2"
                >
                  <div className="grid grid-cols-4 gap-2">
                    <div className="col-span-2">
                      <label className="text-xs text-slate-400">Material</label>
                      <input
                        list={datalistId}
                        type="text"
                        value={mat.name}
                        onChange={(e) => updateMaterial(idx, "name", e.target.value)}
                        placeholder="e.g., white clay"
                        className="w-full text-xs border rounded px-2 py-1 bg-slate-800/60 border-white/10 text-slate-100 placeholder:text-slate-600"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400">Qty</label>
                      <input
                        type="number"
                        min="0"
                        value={mat.qty}
                        onChange={(e) => updateMaterial(idx, "qty", e.target.value)}
                        placeholder="0"
                        className="w-full text-xs border rounded px-2 py-1 bg-slate-800/60 border-white/10 text-slate-100"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400">Unit</label>
                      <select
                        value={mat.unit}
                        onChange={(e) => updateMaterial(idx, "unit", e.target.value)}
                        className="w-full text-xs border rounded px-1 py-1 bg-slate-800/60 border-white/10 text-slate-100"
                      >
                        <option>g</option>
                        <option>kg</option>
                        <option>ml</option>
                        <option>L</option>
                        <option>pc</option>
                      </select>
                    </div>
                  </div>

                  {/* Total calculation */}
                  {mat.qty && (
                    <div className="text-xs text-slate-300 bg-slate-900/50 rounded px-2 py-1 flex justify-between">
                      <span>Total needed:</span>
                      <span className="font-semibold">
                        {mat.total} {mat.unit}
                      </span>
                    </div>
                  )}

                  {/* Remove button */}
                  {materialsTotal.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeMaterial(idx)}
                      className="text-xs w-full px-2 py-1 rounded border border-red-500/40 text-red-300 hover:bg-red-900/20 transition"
                    >
                      Remove
                    </button>
                  )}
                </motion.div>
              ))}

              <datalist id={datalistId}>
                {inv.map((m) => (
                  <option key={m.id} value={m.name || ""} />
                ))}
              </datalist>
            </div>
          ) : (
            <p className="text-xs text-slate-400">No materials added yet</p>
          )}
        </div>

        {/* ===== Action Buttons ===== */}
        <div className="flex gap-2 pt-2">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-4 py-2 rounded font-semibold transition bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? "Creating…" : "Create Workshop"}
          </button>
        </div>
      </form>
    </motion.div>
  );
}
